import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, RefreshCw, Shield, AlertTriangle, CheckCircle, XCircle, Key, Loader2, ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardInstagram() {
  const navigate = useNavigate();
  const [tokenStatus, setTokenStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState('');
  const [updating, setUpdating] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/instagram/token-status`, { headers });
      setTokenStatus(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento dello stato del token');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateToken = async () => {
    if (!newToken.trim()) {
      toast.error('Inserisci un token valido');
      return;
    }
    setUpdating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/instagram/update-token`, { access_token: newToken }, { headers });
      toast.success('Token Instagram aggiornato con successo!');
      setNewToken('');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'aggiornamento del token');
    } finally {
      setUpdating(false);
    }
  };

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/instagram/renew-token`, {}, { headers });
      toast.success(res.data.message || 'Token rinnovato!');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rinnovo fallito - il token potrebbe essere scaduto');
    } finally {
      setRenewing(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/instagram/sync`, {}, { headers });
      toast.success(res.data.message || 'Sync completato!');
    } catch (err) {
      toast.error('Errore nella sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = () => {
    if (!tokenStatus) return <XCircle className="w-8 h-8 text-gray-400" />;
    switch (tokenStatus.status) {
      case 'valid': return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'expiring_soon': return <AlertTriangle className="w-8 h-8 text-amber-500" />;
      case 'expired': return <XCircle className="w-8 h-8 text-red-500" />;
      case 'missing': return <XCircle className="w-8 h-8 text-gray-400" />;
      default: return <AlertTriangle className="w-8 h-8 text-amber-500" />;
    }
  };

  const getStatusColor = () => {
    if (!tokenStatus) return 'border-gray-200 bg-gray-50';
    switch (tokenStatus.status) {
      case 'valid': return 'border-green-300 bg-green-50';
      case 'expiring_soon': return 'border-amber-300 bg-amber-50';
      case 'expired': return 'border-red-300 bg-red-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-instagram-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button onClick={() => navigate('/dashboard')} className="text-gray-500 hover:text-gray-700" data-testid="back-to-dashboard">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                <Instagram className="w-10 h-10 inline mr-3 text-[#E4405F]" />
                Gestione Instagram
              </h1>
              <p className="text-gray-600 mt-2">Gestisci il token di accesso e la sincronizzazione dei post</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[#0066b2]" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status Card */}
              <div className={`rounded-2xl border-2 p-8 ${getStatusColor()}`} data-testid="token-status-card">
                <div className="flex items-start gap-4">
                  {getStatusIcon()}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                      Stato Token
                    </h2>
                    <p className={`text-lg font-medium ${
                      tokenStatus?.status === 'valid' ? 'text-green-700' :
                      tokenStatus?.status === 'expiring_soon' ? 'text-amber-700' :
                      'text-red-700'
                    }`} data-testid="token-status-message">
                      {tokenStatus?.message || 'Stato non disponibile'}
                    </p>
                    
                    {tokenStatus?.has_token && (
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        {tokenStatus.source && (
                          <div>
                            <span className="text-gray-500 block">Origine</span>
                            <span className="font-medium text-gray-800">{tokenStatus.source}</span>
                          </div>
                        )}
                        {tokenStatus.updated_at && (
                          <div>
                            <span className="text-gray-500 block">Ultimo aggiornamento</span>
                            <span className="font-medium text-gray-800">
                              {new Date(tokenStatus.updated_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                        {tokenStatus.days_remaining !== null && tokenStatus.days_remaining !== undefined && (
                          <div>
                            <span className="text-gray-500 block">Giorni rimanenti</span>
                            <span className={`font-bold text-lg ${tokenStatus.days_remaining < 15 ? 'text-red-600' : 'text-green-600'}`}>
                              {tokenStatus.days_remaining}
                            </span>
                          </div>
                        )}
                        {tokenStatus.instagram_user && (
                          <div>
                            <span className="text-gray-500 block">Account Instagram</span>
                            <span className="font-medium text-gray-800">@{tokenStatus.instagram_user.username || tokenStatus.instagram_user.id}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Renew Token */}
                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6" data-testid="renew-token-card">
                  <div className="flex items-center gap-3 mb-4">
                    <RefreshCw className="w-6 h-6 text-[#0066b2]" />
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Rinnova Token</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Rinnova automaticamente il token corrente per altri 60 giorni. Funziona solo se il token attuale non e' ancora scaduto.
                  </p>
                  <Button
                    onClick={handleRenew}
                    disabled={renewing || !tokenStatus?.has_token}
                    className="w-full bg-[#0066b2] hover:bg-[#005494]"
                    data-testid="renew-token-btn"
                  >
                    {renewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Rinnova Ora
                  </Button>
                </div>

                {/* Sync Posts */}
                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6" data-testid="sync-posts-card">
                  <div className="flex items-center gap-3 mb-4">
                    <Instagram className="w-6 h-6 text-[#E4405F]" />
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Sincronizza Post</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Svuota la cache e scarica i post piu' recenti dal tuo profilo Instagram.
                  </p>
                  <Button
                    onClick={handleSync}
                    disabled={syncing || !tokenStatus?.has_token}
                    className="w-full bg-[#E4405F] hover:bg-[#C13584]"
                    data-testid="sync-posts-btn"
                  >
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Instagram className="w-4 h-4 mr-2" />}
                    Sincronizza Ora
                  </Button>
                </div>
              </div>

              {/* Manual Token Update */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 p-8" data-testid="update-token-card">
                <div className="flex items-center gap-3 mb-4">
                  <Key className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Aggiorna Token Manualmente</h3>
                </div>
                <p className="text-gray-600 text-sm mb-2">
                  Se il token e' scaduto, generane uno nuovo dal{' '}
                  <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-[#0066b2] underline hover:text-[#005494]">
                    Facebook Graph API Explorer
                  </a>{' '}
                  e incollalo qui sotto.
                </p>
                <p className="text-amber-700 text-xs mb-4 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Il token verra' validato prima del salvataggio e memorizzato in modo sicuro nel database.
                </p>
                <div className="flex gap-3">
                  <Input
                    value={newToken}
                    onChange={(e) => setNewToken(e.target.value)}
                    placeholder="Incolla qui il nuovo Instagram Access Token..."
                    className="flex-1 font-mono text-sm"
                    type="password"
                    data-testid="new-token-input"
                  />
                  <Button
                    onClick={handleUpdateToken}
                    disabled={updating || !newToken.trim()}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="update-token-btn"
                  >
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiorna'}
                  </Button>
                </div>
              </div>

              {/* Renewal Log */}
              {tokenStatus?.last_renewal && (
                <div className="bg-white rounded-2xl border-2 border-gray-200 p-6" data-testid="renewal-log">
                  <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk' }}>Ultimo Rinnovo Automatico</h3>
                  <div className="flex items-center gap-3 text-sm">
                    {tokenStatus.last_renewal.success ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={tokenStatus.last_renewal.success ? 'text-green-700' : 'text-red-700'}>
                      {tokenStatus.last_renewal.success ? 'Riuscito' : 'Fallito'}
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600">
                      {new Date(tokenStatus.last_renewal.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {tokenStatus.last_renewal.error && (
                      <>
                        <span className="text-gray-400">|</span>
                        <span className="text-red-600 text-xs">{tokenStatus.last_renewal.error}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>Come funziona il rinnovo automatico</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>Il sistema controlla il token ogni lunedi alle 3:00 UTC</li>
                  <li>Se il token scade entro 15 giorni, viene rinnovato automaticamente</li>
                  <li>I token Instagram Long-Lived durano 60 giorni</li>
                  <li>Se il token scade completamente, devi aggiornarlo manualmente</li>
                  <li>Dopo ogni aggiornamento, usa "Sincronizza Post" per ricaricare i contenuti</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
